import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import {
  Box,
  Text,
  Badge,
  Group,
  Button,
  Stack,
  Blockquote,
  Progress,
  Modal,
  Textarea
} from '@mantine/core'
import { useStore } from '../../app/stores/store'
import { EmptyState, AmmoScoreBadge } from '../../app/common/ui'
import { FlagTargetType, ResolveAction } from '../../app/models/flag'
import { toast } from 'react-toastify'

const TARGET_TYPE_LABEL: Record<number, string> = {
  [FlagTargetType.Post]: 'Post',
  [FlagTargetType.Comment]: 'Comment',
  [FlagTargetType.Part]: 'Part',
  [FlagTargetType.FirearmModel]: 'Firearm model',
  [FlagTargetType.Build]: 'Build',
  [FlagTargetType.PostComment]: 'Post comment'
}

const TARGET_TYPE_COLOR: Record<number, string> = {
  [FlagTargetType.Post]: 'blue',
  [FlagTargetType.Comment]: 'orange',
  [FlagTargetType.Part]: 'green',
  [FlagTargetType.FirearmModel]: 'navy',
  [FlagTargetType.Build]: 'gray',
  [FlagTargetType.PostComment]: 'grape'
}

function getResolutionPlaceholder (action: string): string {
  if (action === ResolveAction.Dismiss) return 'Optional note...'
  if (action === ResolveAction.Warn) return 'Describe the warning reason...'
  if (action === ResolveAction.Escalate) return 'Why are you escalating this?'
  return 'Required resolution...'
}

interface ResolveModalProps {
  resolveModal: { action: string } | null;
  resolveModalTitle: string;
  resolveButtonColor: string;
  resolution: string;
  setResolution: (s: string) => void;
  setResolveModal: (v: { action: string } | null) => void;
  submitting: boolean;
  removeMessage: string;
  targetOwnerUsername: string;
  onConfirm: () => Promise<void>;
}

function ResolveModal ({
  resolveModal,
  resolveModalTitle,
  resolveButtonColor,
  resolution,
  setResolution,
  setResolveModal,
  submitting,
  removeMessage,
  targetOwnerUsername,
  onConfirm
}: ResolveModalProps) {
  const handleClose = () => {
    setResolveModal(null)
    setResolution('')
  }
  if (!resolveModal) return null
  const message =
    resolveModal.action === ResolveAction.Dismiss
      ? 'Dismiss this flag with no action taken.'
      : resolveModal.action === ResolveAction.Warn
        ? `Issue a warning to @${targetOwnerUsername}. This will be logged and affect their AmmoScore.`
        : resolveModal.action === ResolveAction.Remove
          ? removeMessage
          : 'Escalate this flag to admin review.'
  return (
    <Modal opened={true} title={resolveModalTitle} onClose={handleClose}>
      <Stack>
        <Text size="sm" color="dimmed">
          {message}
        </Text>
        <Textarea
          label="Resolution"
          placeholder={getResolutionPlaceholder(resolveModal.action)}
          value={resolution}
          onChange={(e) => setResolution(e.currentTarget.value)}
          minRows={3}
          required={resolveModal.action !== ResolveAction.Dismiss}
        />
        <Group position="right">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color={resolveButtonColor}
            onClick={onConfirm}
            loading={submitting}
            disabled={resolveModal.action !== ResolveAction.Dismiss && !resolution.trim()}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default observer(function FlagPreviewPane () {
  const { moderationStore } = useStore()
  const { selectedFlag, resolveFlag, loadFlags } = moderationStore
  const [resolveModal, setResolveModal] = useState<{ action: string } | null>(null)
  const [resolution, setResolution] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleConfirmResolve = async () => {
    if (!selectedFlag || !resolveModal) return
    const action = resolveModal.action
    const required = action !== ResolveAction.Dismiss
    if (required && !resolution.trim()) return
    setSubmitting(true)
    try {
      await resolveFlag(selectedFlag.id, action, resolution.trim())
      moderationStore.selectFlag(null)
      loadFlags(moderationStore.filters)
      if (action === ResolveAction.Dismiss) toast.success('Flag dismissed')
      else if (action === ResolveAction.Warn) toast.warning('Warning issued')
      else if (action === ResolveAction.Remove) toast.error('Content removed / action logged')
      else if (action === ResolveAction.Escalate) toast.info('Flag escalated')
    } catch {
      // interceptor
    } finally {
      setSubmitting(false)
      setResolveModal(null)
      setResolution('')
    }
  }

  const openResolve = (action: string) => {
    setResolveModal({ action })
    setResolution('')
  }

  if (!selectedFlag) {
    return (
      <EmptyState
        title="Select a flag"
        message="Choose a flag from the list to review it here."
      />
    )
  }

  const f = selectedFlag
  const accuracy = f.reporterFlagsSubmitted > 0
    ? Math.round((f.reporterFlagsUpheld / f.reporterFlagsSubmitted) * 100)
    : 0
  const accuracyColor = accuracy > 70 ? 'green' : accuracy < 30 ? 'yellow' : 'blue'

  const removeMessage = f.targetCanBeHidden
    ? `Hide this content from non-moderators and log a moderation action against @${f.targetOwnerUsername}. This will affect their AmmoScore.`
    : `Log a moderation action against @${f.targetOwnerUsername}. This content type cannot be hidden but the action will be recorded.`

  const resolveModalTitle =
    resolveModal?.action === ResolveAction.Dismiss
      ? 'Dismiss flag'
      : resolveModal?.action === ResolveAction.Warn
        ? 'Warn user'
        : resolveModal?.action === ResolveAction.Remove
          ? 'Remove content'
          : 'Escalate'

  const resolveButtonColor =
    resolveModal?.action === ResolveAction.Dismiss
      ? 'gray'
      : resolveModal?.action === ResolveAction.Warn
        ? 'yellow'
        : resolveModal?.action === ResolveAction.Remove
          ? 'red'
          : 'violet'

  return (
    <Stack spacing="lg">
      {/* Section A — Flagged Content */}
      <Box>
        <Group spacing="xs" mb="xs">
          <Badge color={TARGET_TYPE_COLOR[f.targetType]} size="sm">
            {TARGET_TYPE_LABEL[f.targetType]}
          </Badge>
          <Text weight={600} size="md">
            {f.targetTitle || '—'}
          </Text>
        </Group>
        <Box
          sx={{
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-gray-0)'
          }}
        >
          <Text size="xs" color="dimmed" mb={4}>
            @{f.targetOwnerUsername}
          </Text>
          <Group spacing="xs">
            <AmmoScoreBadge tier={f.targetOwnerTier} score={f.targetOwnerAmmoScore ?? 0} size="sm" />
            <Text size="xs" color="dimmed">
              {f.targetOwnerFlagsAgainst} previous flags on their content
            </Text>
          </Group>
        </Box>
        <Blockquote mt="sm" cite={null} styles={{ cite: { display: 'none' } }}>
          <Text size="sm">{f.reason}</Text>
        </Blockquote>
        {f.groupCount > 1 && (
          <Box
            mt="sm"
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-yellow-0)',
              border: '1px solid var(--mantine-color-yellow-3)'
            }}
          >
            <Text size="sm">
              This content has been reported {f.groupCount} times. Resolving this flag will not auto-resolve the others.
            </Text>
          </Box>
        )}
        {!f.targetCanBeHidden && (
          <Box
            mt="sm"
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-gray-1)',
              border: '1px solid var(--mantine-color-gray-3)'
            }}
          >
            <Text size="sm" color="dimmed">
              Note: this content type cannot be hidden. The Remove action will resolve the flag and log it but will not hide the content.
            </Text>
          </Box>
        )}
      </Box>

      {/* Section B — Reporter Context */}
      <Box>
        <Text size="sm" weight={600} mb="xs">
          Reporter context
        </Text>
        <Box
          sx={{
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-gray-0)'
          }}
        >
          <Text size="xs" color="dimmed">
            Reported by @{f.reporterUsername}
          </Text>
          <Group spacing="xs" mt={4}>
            <AmmoScoreBadge tier={f.reporterTier} score={f.reporterAmmoScore ?? 0} size="sm" />
            <Text size="xs">
              {f.reporterFlagsSubmitted} flags submitted · {f.reporterFlagsUpheld} upheld
            </Text>
          </Group>
          <Text size="xs" mt="xs">
            Flag accuracy rate: {accuracy}%
          </Text>
          <Progress value={accuracy} color={accuracyColor} size="sm" mt={4} />
        </Box>
      </Box>

      {/* Section C — Action Buttons */}
      <Box>
        <Text size="sm" weight={600} mb="xs">
          Actions
        </Text>
        <Group spacing="sm">
          <Button variant="light" color="gray" onClick={() => openResolve(ResolveAction.Dismiss)}>
            Dismiss
          </Button>
          <Button variant="light" color="yellow" onClick={() => openResolve(ResolveAction.Warn)}>
            Warn User
          </Button>
          <Button
            variant="light"
            color="red"
            onClick={() => openResolve(ResolveAction.Remove)}
            title={!f.targetCanBeHidden ? 'Cannot hide this content type — flag will be logged only.' : undefined}
            sx={!f.targetCanBeHidden ? { textDecoration: 'line-through' } : undefined}
          >
            Remove
          </Button>
          <Button variant="light" color="violet" onClick={() => openResolve(ResolveAction.Escalate)}>
            Escalate
          </Button>
        </Group>
      </Box>

      {/* Resolve modal with textarea */}
      <ResolveModal
        resolveModal={resolveModal}
        resolveModalTitle={resolveModalTitle}
        resolveButtonColor={resolveButtonColor}
        resolution={resolution}
        setResolution={setResolution}
        setResolveModal={setResolveModal}
        submitting={submitting}
        removeMessage={removeMessage}
        targetOwnerUsername={f.targetOwnerUsername}
        onConfirm={handleConfirmResolve}
      />
    </Stack>
  )
})
